/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import {
  empty,
  errorResponse,
  isArray,
  isObject,
  prepareResponseData,
} from "../../Utilities/utils";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

// css
import "../students/Students.css";
import "../dashboard/Dashboard.css";

// api
import classApi from "../../api/Classes";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import Card from "../../components/card/Card";
import StatCard from "../../components/statcard/StatCard";
import DoughnutChart from "../../components/chart/doughnut/DoughnutChart";
import {
  FaBookReader,
  FaChartArea,
  FaChartLine,
  FaChartPie,
  FaCheckDouble,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaExclamationTriangle,
  FaFileDownload,
  FaForward,
  FaRegPlusSquare,
  FaSync,
  FaUserGraduate,
  FaUserPlus,
  FaUserTag,
} from "react-icons/fa";
import { useContext } from "react";
import { AuthContext } from "../Root/ProtectedRoute";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import BoxLoading from "../../components/skeleton/BoxLoading";
import DonutChartLoading from "../../components/skeleton/DonutChartLoading";
import TableLoading from "../../components/skeleton/TableLoading";
import FullPageLoader from "../../components/loader/FullPageLoader";
import colors from "../../config/colors";
import CopyTextButton from "../../components/buttons/floatingButton/CopyTextButton";
import DownloadCSV from "../../components/utility/DownloadCSV";

const ClassProfile = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const [noOfStudentsLoading, setNoOfStudentsLoading] = useState(false);
  const [classData, setClassData] = useState({});
  const [numOfStudents, setNumOfStudents] = useState(0);
  const [numOfSubjects, setNumOfSubjects] = useState(0);
  const [students, setStudents] = useState([]);
  const [genderCounts, setGenderCounts] = useState({ male: 0, female: 0 });
  const [subjects, setSubjects] = useState([]);
  const [assessmentPercentage, setAssessmentPercentage] = useState(0);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [subjectsInClassLoading, setSubjectsInClassLoading] = useState(false);
  const [noOfSubjectsLoading, setNoOfSubjectsLoading] = useState(false);
  const [assessmentPercentageLoading, setAssessmentPercentageLoading] =
    useState(false);
  const [classAttendanceCount, setClassAttendanceCount] = useState(0);
  const [classPerformanceLoading, setClassPerformanceLoading] = useState(false);
  const [noOfStudentsByGenderLoading, setNoOfStudentsByGenderLoading] =
    useState(false);
  const [isDropDown, setIsDropDown] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false); // (WIP)
  const { classId } = useParams();
  const toastTR = useRef(null);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  useEffect(() => {
    if (!classId) {
      navigate("/404");
    }
    checkAuthorization();
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      getNumberOfStudentsByGender();
      getAssessmentPercentage();
      getNumOfSubjects();
      getNumOfStudents();
      getClassDetails();
      getSubjectsInClass();
      getStudentsInClassPerformance();
      getClassStudentAttendanceCount();
    }
  }, [isAuthorized, classId, user?._id]);

  const checkAuthorization = () => {
    try {
      let authorizedClasses = [];

      // check if user is authorized to view class details
      const userAuthorizations =
        !empty(user) && !empty(user.authorizations) ? user.authorizations : [];
      const classesObj = userAuthorizations.find((obj) =>
        obj.hasOwnProperty("classes"),
      );
      authorizedClasses =
        !empty(classesObj) && !empty(classesObj.classes)
          ? classesObj.classes
          : [];
      if (
        !empty(authorizedClasses) &&
        (authorizedClasses.includes("All") ||
          authorizedClasses.includes(classId))
      ) {
        setIsAuthorized(true);
      }
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    }
  };

  const getClassDetails = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getSingleClass(classId, schoolId);
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data) ||
        !response_data.response
      ) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Something went wrong",
        );
      }
      setClassData(
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : {},
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  const getSubjectsInClass = async () => {
    try {
      if (!subjectsInClassLoading) setSubjectsInClassLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const limit = 6;
      const response = await classApi.getSubjectsInClass(
        classId,
        schoolId,
        limit,
      );
      const response_data = prepareResponseData(response);
      if (
        empty(response_data) ||
        empty(response_data.response) ||
        !response_data.response
      ) {
        return setSubjects([]);
      }
      setSubjects(
        !empty(response_data) &&
          !empty(response_data.response) &&
          isArray(response_data.response)
          ? response_data.response
          : [],
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setSubjectsInClassLoading(false);
    }
  };

  const getNumOfStudents = async () => {
    try {
      if (!noOfStudentsLoading) setNoOfStudentsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getNumOfStudents(classId, schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return;
      }
      setNumOfStudents(
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : 0,
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setNoOfStudentsLoading(false);
    }
  };

  /**
   * Get class student attendance count
   * @returns
   */
  const getClassStudentAttendanceCount = async () => {
    try {
      if (!noOfStudentsLoading) setNoOfStudentsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.studentAttendanceCount(classId, schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return;
      }
      const count = response_data?.response?.attendanceCount || 0;
      const percentage = response_data?.response?.percentage || 0;
      setClassAttendanceCount(`${count} (${percentage}%)`);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setNoOfStudentsLoading(false);
    }
  };

  const getNumOfSubjects = async () => {
    try {
      if (!noOfSubjectsLoading) setNoOfSubjectsLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getNumOfClassSubjects(classId, schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setNumOfSubjects(0);
      }
      setNumOfSubjects(
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : 0,
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setNoOfSubjectsLoading(false);
    }
  };

  const getAssessmentPercentage = async () => {
    try {
      if (!assessmentPercentageLoading) setAssessmentPercentageLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getAssessmentPercentage(
        classId,
        schoolId,
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return;
      }
      setAssessmentPercentage(
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : 0,
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setAssessmentPercentageLoading(false);
    }
  };

  const getStudentsInClassPerformance = async () => {
    try {
      if (!classPerformanceLoading) setClassPerformanceLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const limit = 10;
      const response = await classApi.getStudentsInClassPerformance(
        classId,
        schoolId,
        null,
        null,
        limit,
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return setStudents([]);
      }
      setStudents(
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : [],
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setClassPerformanceLoading(false);
    }
  };

  const getNumberOfStudentsByGender = async () => {
    try {
      if (!noOfStudentsByGenderLoading) setNoOfStudentsByGenderLoading(true);
      const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
      const response = await classApi.getNumberOfStudentsByGender(
        classId,
        schoolId,
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        return;
      }
      setGenderCounts(
        !empty(response_data) &&
          !empty(response_data.response) &&
          isObject(response_data.response)
          ? response_data.response
          : { male: 0, female: 0 },
      );
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setNoOfStudentsByGenderLoading(false);
    }
  };

  const reconcileWithMainClass = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await classApi.syncWithMainClass(
        { class_id: classId }
      );
      const response_data = prepareResponseData(response);
      if (!response_data?.success) {
        return responseDialog("error", "Error Alert!", "Failed to sync sub/stream class with main class.");
      }
      
      return responseDialog('success', 'Success', "Sync successful.");
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  }

  const title =
    !empty(classData) && !empty(classData.title) ? classData.title : "";

  const performanceBodyTemplate = (rowData) => {
    const score =
      !empty(rowData) && !empty(rowData.performanceScore)
        ? rowData.performanceScore
        : 0;

    let score_tag = "";
    if (score >= 70) {
      score_tag = <span style={{ color: "green" }}>{score}%</span>;
    } else if (score >= 60 && score <= 69.9) {
      score_tag = <span style={{ color: "#633ccd" }}>{score}%</span>;
    } else if (score >= 50 && score <= 59.9) {
      score_tag = <span style={{ color: "orange" }}>{score}%</span>;
    } else if (score >= 45 && score <= 49.9) {
      score_tag = <span style={{ color: "gray" }}>{score}%</span>;
    } else {
      score_tag = <span style={{ color: "red" }}>{score}%</span>;
    }

    return score_tag;
  };

  const toggleDropdown = () => {
    setIsDropDown(!isDropDown);
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          {isAuthorized ? (
            <>
              {/* menu header */}
              <MainHeader title={
                <span>{title} Profile { 
                  classData?.isStream === true && 
                    <>
                    (Sub class of { classData?.mainClassTitle && classData?.mainClassId ? <NavLink to={`/classes/${classData.mainClassId}/profile`} style={{ color: colors.primary, fontWeight: 'bold' }}>{classData.mainClassTitle}</NavLink> : '' })
                    </>
                }
                </span>
              } />

              <div className="mt-20 mb-10 flex flex-wrap">
                <ButtonIcon
                  borderColor={colors.primary}
                  backgroundColor={colors.primary}
                  color={colors.white}
                  buttonText="Students"
                  fontSize={13}
                  width={150}
                  icon={<FaUserGraduate size={12} />}
                  iconMarginRight={8}
                  marginRight={1}
                  height={30}
                  marginBottom={2}
                  onClick={() => navigate(`/students/class/${classId}`)}
                />
                { (classData?.mainClassId && classData?.isStream === true) &&
                  <ButtonIcon
                    borderColor={colors.primary}
                    backgroundColor={colors.primary}
                    color={colors.white}
                    buttonText="Assign Students"
                    fontSize={13}
                    width={160}
                    icon={<FaUserPlus size={12} />}
                    iconMarginRight={8}
                    marginRight={1}
                    height={30}
                    marginBottom={2}
                    onClick={() => navigate(`/students/class/${classData.mainClassId}/stream/${classId}/assign`)}
                  />
                }
                <ButtonIcon
                  borderColor={colors.primary}
                  backgroundColor={colors.primary}
                  color={colors.white}
                  buttonText="Assessment"
                  fontSize={13}
                  width={150}
                  icon={<FaChartLine size={12} />}
                  iconMarginRight={8}
                  marginRight={1}
                  height={30}
                  marginBottom={2}
                  onClick={() =>
                    navigate("/assessment/record", {
                      state: { classId },
                    })
                  }
                />
                {user?.currentSessionId && user?.currentTermId && (
                  <>
                    <ButtonIcon
                      borderColor={colors.primary}
                      backgroundColor={colors.primary}
                      color={colors.white}
                      buttonText="Class Results"
                      fontSize={13}
                      width={180}
                      icon={<FaChartPie size={12} />}
                      iconMarginRight={8}
                      marginRight={1}
                      height={30}
                      marginBottom={2}
                      onClick={() =>
                        navigate("/assessment/report-sheet", {
                          state: {
                            sessionId: user.currentSessionId,
                            termId: user.currentTermId,
                            classId,
                          },
                        })
                      }
                    />
                    <ButtonIcon
                      borderColor={colors.primary}
                      backgroundColor={colors.primary}
                      color={colors.white}
                      buttonText="Download Class Results"
                      width={240}
                      fontSize={13}
                      icon={<FaFileDownload size={12} />}
                      iconMarginRight={8}
                      marginRight={1}
                      height={30}
                      marginBottom={2}
                      onClick={() =>
                        navigate(
                          `/student/report-sheet/session/${user.currentSessionId}/term/${user.currentTermId}/class/${classId}`,
                        )
                      }
                    />
                  </>
                )}
                { classData?.isStream !== true && 
                  <ButtonIcon
                    borderColor={colors.primary}
                    backgroundColor={colors.primary}
                    color={colors.white}
                    buttonText="Promotion"
                    icon={<FaForward size={12} />}
                    iconMarginRight={8}
                    fontSize={13}
                    width={150}
                    height={30}
                    marginRight={1}
                    marginBottom={2}
                    onClick={() =>
                      navigate(`/students/class/${classId}/promotion`)
                    }
                  />
                }
                <ButtonIcon
                  borderColor={colors.primary}
                  backgroundColor={colors.primary}
                  color={colors.white}
                  buttonText="Photo Cards"
                  fontSize={13}
                  icon={<FaUserTag size={12} />}
                  iconMarginRight={8}
                  marginRight={1}
                  width={180}
                  height={30}
                  marginBottom={2}
                  onClick={() =>
                    navigate(`/students/class/${classId}/bio-data`)
                  }
                />
                <ButtonIcon
                  borderColor={colors.primary}
                  backgroundColor={colors.primary}
                  color={colors.white}
                  buttonText="Attendance"
                  iconMarginRight={8}
                  icon={<FaCheckDouble size={12} />}
                  width={150}
                  height={30}
                  marginRight={1}
                  marginBottom={2}
                  onClick={() =>
                    navigate(`/attendance/history`, {
                      replace: true,
                      state: { classId },
                    })
                  }
                />
                <ButtonIcon
                  borderColor={colors.primary}
                  backgroundColor={colors.primary}
                  color={colors.white}
                  buttonText="Time Table"
                  iconMarginRight={8}
                  icon={<FaClock size={12} />}
                  width={150}
                  height={30}
                  marginRight={1}
                  marginBottom={2}
                  onClick={() =>
                    navigate(`/time-table/home`, {
                      replace: true,
                      state: { classId },
                    })
                  }
                />
                { classData?.isStream && 
                  <ButtonIcon
                    borderColor={colors.primary}
                    backgroundColor={colors.primary}
                    color={colors.white}
                    buttonText="Sync with Main Class"
                    iconMarginRight={8}
                    icon={<FaSync size={12} />}
                    width={200}
                    height={30}
                    marginRight={1}
                    marginBottom={2}
                    onClick={() => reconcileWithMainClass()}
                  />
                }
                <div className="class-profile-dropdown-container">
                  <ButtonIcon
                    borderColor={colors.primary}
                    backgroundColor={colors.primary}
                    color={colors.white}
                    buttonText="Report"
                    iconRight={
                      isDropDown ? (
                        <FaChevronDown size={12} />
                      ) : (
                        <FaChevronUp size={12} />
                      )
                    }
                    width={100}
                    height={30}
                    iconMarginLeft={12}
                    marginBottom={2}
                    className="class-profile-dropdown-button"
                    onClick={() => toggleDropdown()}
                  />
                  {isDropDown && (
                    <div className="class_profile_dropdown_content">
                      <ul>
                        <li
                          onClick={() =>
                            navigate(`/class/${classId}/broad-sheet`)
                          }
                        >
                          Exam Broadsheet
                        </li>
                        <li
                          onClick={() =>
                            navigate(`/students/class/${classId}/reports`)
                          }
                        >
                          Student List
                        </li>
                        <li
                          onClick={() =>
                            navigate(`/students/form`)
                          }
                        >
                          Students Form
                        </li>
                        <li
                          onClick={() =>
                            navigate(`/students/subject/assessment-sheets`)
                          }
                        >
                          Assessment Sheets
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              {/* end menu header */}
              <div
                className="mt-10"
                style={{ display: "flex", flexDirection: "row" }}
              >
                {/* left box */}
                <div id="main_left_box">
                  {/* stat box */}
                  <div className="top_stat_box">
                    <Card
                      children={
                        !noOfStudentsLoading ? (
                          <StatCard
                            underlineWidth={15}
                            title="Students"
                            entry={numOfStudents}
                            icon={
                              <FaUserGraduate
                                size={14}
                                className="stat_card_icon"
                              />
                            }
                          />
                        ) : (
                          <BoxLoading />
                        )
                      }
                      addStyle="card_adjust_stat"
                    />
                    <Card
                      children={
                        !noOfSubjectsLoading ? (
                          <StatCard
                            underlineWidth={15}
                            title="Subjects"
                            entry={numOfSubjects}
                            icon={
                              <FaBookReader
                                size={14}
                                className="stat_card_icon"
                              />
                            }
                          />
                        ) : (
                          <BoxLoading />
                        )
                      }
                      addStyle="card_adjust_stat"
                    />
                    <Card
                      children={
                        !assessmentPercentageLoading ? (
                          <StatCard
                            underlineWidth={15}
                            title="Assessment"
                            entry={assessmentPercentage + "%"}
                            icon={
                              <FaChartLine
                                size={14}
                                className="stat_card_icon"
                              />
                            }
                          />
                        ) : (
                          <BoxLoading />
                        )
                      }
                      addStyle="card_adjust_stat"
                    />
                    <Card
                      children={
                        <StatCard
                          underlineWidth={15}
                          title="Attendance"
                          entry={classAttendanceCount}
                          icon={
                            <FaChartArea size={14} className="stat_card_icon" />
                          }
                        />
                      }
                      addStyle="card_adjust_stat"
                    />
                  </div>
                  {/* stat header end */}

                  {/* chart */}
                  <div className="inner_chart_box">
                    <Card
                      children={
                        <>
                          <MainHeader title="Students Stat By Gender" />
                          {!noOfStudentsByGenderLoading ? (
                            <DoughnutChart
                              doughnutData={[
                                !empty(genderCounts) &&
                                !empty(genderCounts.male)
                                  ? genderCounts.male
                                  : 0,
                                !empty(genderCounts) &&
                                !empty(genderCounts.female)
                                  ? genderCounts.female
                                  : 0,
                              ]}
                            />
                          ) : (
                            <DonutChartLoading />
                          )}
                        </>
                      }
                      addStyle="px-20 agenda-box"
                      id="chart_box1"
                    />
                    <Card
                      children={
                        <>
                          <MainHeader
                            title="Subjects"
                            rightIcon={
                              <NavLink to={`/class/${classId}/subjects/add`}>
                                <FaRegPlusSquare
                                  style={{
                                    color: colors.primary,
                                    cursor: "pointer",
                                  }}
                                />
                              </NavLink>
                            }
                          />

                          {!subjectsInClassLoading ? (
                            <DataTable
                              value={subjects}
                              tableStyle={{ minWidth: "10rem" }}
                            >
                              <Column field="code" header="Code"></Column>
                              <Column field="title" header="Title"></Column>
                            </DataTable>
                          ) : (
                            <TableLoading rows={6} cols={2} header={false} />
                          )}
                        </>
                      }
                      id="chart_box2"
                    />
                  </div>
                  <div className="chart_box">
                    <Card
                      children={
                        <>
                          <MainHeader
                            title="Top 10 Students"
                            rightIcon={
                              <NavLink
                                to={`/class/${classId}/students/performance`}
                              >
                                <FaRegPlusSquare
                                  style={{
                                    color: colors.primary,
                                    cursor: "pointer",
                                  }}
                                />
                              </NavLink>
                            }
                          />
                          <div className="dashboard_school_list">
                            {!classPerformanceLoading ? (
                              <DataTable
                                value={students}
                                tableStyle={{ minWidth: "30rem" }}
                              >
                                <Column
                                  field="regNo"
                                  header="Reg. No."
                                ></Column>
                                <Column
                                  field="fullName"
                                  header="Full Name"
                                ></Column>
                                <Column field="total" header="Total"></Column>
                                <Column
                                  field="performanceScore"
                                  header="Performance"
                                  body={performanceBodyTemplate}
                                ></Column>
                              </DataTable>
                            ) : (
                              <TableLoading rows={10} cols={3} />
                            )}
                          </div>
                        </>
                      }
                      addStyle="student_list_box"
                    />
                  </div>
                  {/* end chart */}

                  {/*  */}
                </div>
                {/* end of left box */}

                {/* right box */}
                <div id="main_right_box">
                  <div className="main_right_boxes">
                    {/* activities */}
                    <Card
                      children={
                        <>
                          <MainHeader
                            title="Activities"
                            rightIcon={
                              <NavLink to={`/activities/home`}>
                                <FaRegPlusSquare
                                  style={{
                                    color: colors.primary,
                                    cursor: "pointer",
                                  }}
                                />
                              </NavLink>
                            }
                          />
                          {/* {ActivityData.map((activity) => {
                            return (
                              <ListTitleSubtitle
                                key={activity.id}
                                date={activity.date}
                                time={activity.time}
                                title={activity.title}
                                subtitle={activity.subtitle}
                                borderColor={activity.color}
                              />
                            );
                          })} */}
                        </>
                      }
                      addStyle="card_adjust"
                    />
                    {/* end of activities */}
                  </div>
                </div>
                {/* end of right box */}
              </div>
            </>
          ) : (
            <div className="inner-unauthorized">
              <FaExclamationTriangle color="red" size={30} />
              <p>
                You do not have the required authorization to view this page.
              </p>
            </div>
          )}
        </main>
        <Toast ref={toastTR} position="bottom-left" />
        {isLoading && <FullPageLoader visible={isLoading} />}
        { classData?.isStream !== true &&
          <>
            <CopyTextButton
              width={65}
              height={65}
              right={35}
              actionTitle="Copy class Id"
              copyText={classId}
            />
            <DownloadCSV
              width={65}
              height={65}
              right={35}
              actionTitle="Download CSV Template"
              copyText={classId}
              bottom="17%"
              classId={classId}
              classTitle={title}
            />
          </>
        }
      </AppWrapper>
    </>
  );
};

export default ClassProfile;
